import { StandardEvents } from '@shopify/events';

/**
 * Wraps an add-to-cart button and toggles it between a "Shop now" state
 * (adds the currently selected variant to the cart) and a "Go to cart"
 * state (navigates to the cart) based on whether that variant is already
 * in the cart.
 *
 * Stays in sync in real time, without a full page reload:
 * - Listens for `StandardEvents.productSelect` to re-check cart membership
 *   whenever the shopper switches variants (e.g. changes size).
 * - Listens for `StandardEvents.cartLinesUpdate` to re-check whenever the
 *   cart changes from anywhere (this button, the cart page, a cart drawer,
 *   etc.), so removing the variant from the cart flips the button back to
 *   "Shop now" without needing to reload the product page.
 */
class DynamicCtaButton extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    const sectionTarget = this.closest('.shopify-section, dialog, product-card');
    sectionTarget?.addEventListener(StandardEvents.productSelect, this.#onVariantChange, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    // Re-sync when the page is restored from the browser's back-forward
    // cache (e.g. clicking "back" from the cart page), since connectedCallback
    // does not re-run in that case and our state would otherwise go stale.
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    this.addEventListener('click', this.#onClickCapture, { signal, capture: true });

    this.#syncClickBinding();
    this.#syncWithCart();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @returns {HTMLElement | null} */
  get #label() {
    return this.querySelector('.dynamic-cta-button__label');
  }

  /** @returns {HTMLElement | null} */
  get #addToCartButton() {
    return this.querySelector('[ref="addToCartButton"]');
  }

  /** @returns {string | null} */
  get #variantId() {
    return this.closest('form')?.querySelector('input[name="id"]')?.value ?? null;
  }

  /** @returns {boolean} */
  get #inCart() {
    return this.dataset.inCart === 'true';
  }

  /** @param {boolean} value */
  set #inCart(value) {
    if (this.#inCart === value) return;

    this.dataset.inCart = value ? 'true' : 'false';

    const label = this.#label;
    if (label) {
      label.textContent = value ? (this.dataset.inCartLabel ?? '') : (this.dataset.shopNowLabel ?? '');
    }

    this.#syncClickBinding();
  }

  /**
   * The theme's shared `@theme/component` event system dispatches `on:click`
   * via a single document-level listener that resolves and runs before this
   * element's own click handling ever gets a chance to intervene, so once
   * that handler has run there's no stopping the add-to-cart submission (and
   * its fly-to-cart animation) after the fact. Removing the `on:click`
   * attribute while the button is in the "Go to cart" state stops that
   * handler from ever resolving to this button in the first place.
   */
  #syncClickBinding() {
    const button = this.#addToCartButton;
    if (!button) return;

    if (this.#inCart) {
      if (button.hasAttribute('on:click')) {
        button.dataset.dynamicCtaOnClick = button.getAttribute('on:click') ?? '';
        button.removeAttribute('on:click');
      }
    } else if (button.dataset.dynamicCtaOnClick !== undefined) {
      button.setAttribute('on:click', button.dataset.dynamicCtaOnClick);
      delete button.dataset.dynamicCtaOnClick;
    }
  }

  /**
   * Intercepts the click in the capture phase (before the add-to-cart
   * component's own submit handling runs) when the variant is already in
   * the cart, redirecting to the cart instead of adding it again.
   * @param {MouseEvent} event
   */
  #onClickCapture = (event) => {
    if (!this.#inCart) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    // Plain route to the cart, no view transition - the animated transition
    // is reserved for the add-to-cart ("SHOP NOW") action.
    sessionStorage.setItem('skip-view-transition', 'true');
    window.location.href = this.dataset.cartUrl || '/cart';
  };

  /**
   * Handles the page show event. Fires on every page display, whether the
   * page was just freshly loaded or restored from the back-forward cache.
   * We deliberately don't gate this on `event.persisted` (unlike a naive
   * bfcache-only check) because relying on that signal alone has proven
   * unreliable for this cross-document navigation in practice; always
   * re-checking is cheap and guarantees the button never shows stale state.
   */
  #onPageShow = () => {
    this.#syncWithCart();
  };

  /**
   * @param {CustomEvent & {promise?: Promise<{detail?: {variantId?: string, resource?: {id?: string}}}>}} event
   */
  #onVariantChange = (event) => {
    event.promise?.then(() => this.#syncWithCart()).catch(() => {});
  };

  /**
   * @param {CustomEvent & {promise?: Promise<{detail?: {items?: Array<{variant_id: number | string}>}}>}} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.items) {
          this.#applyCartItems(detail.items);
        } else {
          this.#fetchCart().then((cart) => this.#applyCartItems(cart.items));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[dynamic-cta-button] Event promise rejected:', error);
      });
  };

  #syncWithCart() {
    this.#fetchCart()
      .then((cart) => this.#applyCartItems(cart.items))
      .catch(() => {});
  }

  /** @param {Array<{variant_id: number | string}>} items */
  #applyCartItems(items) {
    const variantId = this.#variantId;
    if (!variantId || !items) return;

    const found = items.some((item) => item.variant_id?.toString() === variantId.toString());
    this.#inCart = found;
  }

  #fetchCart() {
    return fetch(`${this.dataset.cartUrl || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }
}

if (!customElements.get('dynamic-cta-button')) {
  customElements.define('dynamic-cta-button', DynamicCtaButton);
}
